import {
  QueryProcessedDataViewsDocument,
  useDeleteProcessedTableMutation,
  useQueryProcessedDataViewsQuery,
} from "graphql/types/graphql";
import { useCallback, useContext } from "react";
import { useSnackbar } from "notistack";
import { context } from "utils/Provider";
interface DeleteProcessedDataResponse {
  success: boolean;
  message: string;
  error?: string;
}

export const useDeleteProcessedData = () => {
  const { selectedCube } = useContext(context);
  const { enqueueSnackbar } = useSnackbar();
  const [deleteProcessedTable, { loading, error }] = useDeleteProcessedTableMutation();

  const deleteTable = useCallback(
    async (tableName: string): Promise<DeleteProcessedDataResponse> => {
      try {
        const response = await deleteProcessedTable({
          variables: { tableName },
          update: (cache, { data }) => {
            if (!data?.deleteProcessedTable?.success) return;

            const queryOptions = {
              query: QueryProcessedDataViewsDocument,
              variables: {
                cubeId: selectedCube.cubeId,
                limit: 10,
                offset: 0,
              },
            };

            const cachedData = cache.readQuery<{
              queryProcessedDataViews: {
                edges: Array<{ name: string; createdAt: string }>;
                totalCount: number;
                hasNextPage: boolean;
                hasPreviousPage: boolean;
              };
            }>(queryOptions);

            if (cachedData?.queryProcessedDataViews) {
              cache.writeQuery({
                ...queryOptions,
                data: {
                  queryProcessedDataViews: {
                    ...cachedData.queryProcessedDataViews,
                    edges: cachedData.queryProcessedDataViews.edges.filter((edge) => edge.name !== tableName),
                    totalCount: cachedData.queryProcessedDataViews.totalCount - 1,
                  },
                },
              });
            }

            cache.gc();
          },
        });

        const result = response.data?.deleteProcessedTable;

        if (result?.success) {
          enqueueSnackbar(`Table '${tableName}' deleted successfully`, {
            variant: "success",
          });

          return {
            success: true,
            message: `Table '${tableName}' deleted successfully`,
          };
        } else {
          enqueueSnackbar(result?.message || "Failed to delete table", {
            variant: "error",
          });

          return {
            success: false,
            message: result?.message || "Failed to delete table",
            error: result?.message || "Unknown error",
          };
        }
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : String(err);
        enqueueSnackbar(`Failed to delete table '${tableName}': ${errorMessage}`, {
          variant: "error",
        });

        return {
          success: false,
          message: `Failed to delete table '${tableName}'`,
          error: errorMessage,
        };
      }
    },
    [deleteProcessedTable, enqueueSnackbar, selectedCube.cubeId]
  );

  return {
    deleteTable,
    loading,
    error: error ? error.message || "Mutation error" : null,
  };
};
